/**
 * Network Service for Poker Reborn
 *
 * Handles WebSocket connection to the poker_platform backend.
 * Implements the GL (GameLink) protocol.
 */

import { EventEmitter } from './EventEmitter';
import {
  CONNECTION,
  DEBUG,
  GLIDS,
  getServerConfig,
} from '@/config/network';
import { log } from '@/utils/log';
import type {
  ConnectionState,
  GLPacket,
} from '@/types/protocol';
import { PacketTypes, Commands, ServiceGLIDs } from '@/types/protocol';
import type { ConnectionDiagnostic } from '@/types/network';
import {
  diagnoseConnectionFailure,
  diagnoseTimeout,
  diagnoseUnknown,
} from './NetworkDiagnostics';

// =============================================================================
// TYPES
// =============================================================================

/** Network service events */
export interface NetworkEvents {
  /** Connection established and authenticated */
  connected: void;

  /** Connection closed */
  disconnected: { code: number; reason: string };

  /** Connection state changed */
  stateChange: ConnectionState;

  /** Message received from server */
  message: { from: string; body: string };

  /** Service message (second_auth, etc.) */
  service: string;

  /** Error occurred */
  error: Error;

  /** Reconnecting */
  reconnecting: { attempt: number; delay: number };

  /** Reconnection successful */
  reconnected: void;

  /** Connection diagnostic available (for detailed error display) */
  connectionDiagnostic: ConnectionDiagnostic;
}

// =============================================================================
// NETWORK SERVICE
// =============================================================================

export class NetworkService extends EventEmitter<NetworkEvents> {
  private static instance: NetworkService | null = null;

  private ws: WebSocket | null = null;
  private state: ConnectionState = 'disconnected';
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private accountKeepAliveTimer: ReturnType<typeof setInterval> | null = null;
  private missedHeartbeats = 0;
  private lastMessageTime = 0;
  private authResolver: ((value: void) => void) | null = null;
  private authRejecter: ((error: Error) => void) | null = null;

  private url: string = '';
  private username: string = '';
  private password: string = '';
  private myGlid: string = '';

  private constructor() {
    super();
  }

  /** Get singleton instance */
  static getInstance(): NetworkService {
    if (!NetworkService.instance) {
      NetworkService.instance = new NetworkService();
    }
    return NetworkService.instance;
  }

  /** Get current connection state */
  getState(): ConnectionState {
    return this.state;
  }

  /** Check if connected and authenticated */
  isConnected(): boolean {
    return this.state === 'connected';
  }

  /** Get player's GLID (after authentication) */
  getMyGlid(): string {
    return this.myGlid;
  }

  /**
   * Connect and authenticate to the server
   */
  async connect(username: string, password: string): Promise<void> {
    if (this.state !== 'disconnected') {
      throw new Error(`Cannot connect: current state is ${this.state}`);
    }

    this.username = username;
    this.password = password;
    this.url = getServerConfig().wsUrl;

    return this.doConnect();
  }

  /**
   * Disconnect from the server
   */
  disconnect(): void {
    this.stopHeartbeat();
    this.clearReconnectTimer();
    this.reconnectAttempt = 0;

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setState('disconnected');
  }

  /**
   * Immediately attempt to reconnect (for "Try Now" button)
   * Cancels any pending reconnect timer and tries immediately.
   */
  async reconnectNow(): Promise<void> {
    // Only allow if we have stored credentials and are disconnected/reconnecting
    if (!this.username || !this.password) {
      this.log('Cannot reconnect: no stored credentials');
      return;
    }

    if (this.state === 'connected' || this.state === 'connecting') {
      this.log('Cannot reconnect: already connected/connecting');
      return;
    }

    // Cancel any pending scheduled reconnect
    this.clearReconnectTimer();

    // Reset attempt counter for fresh start
    this.reconnectAttempt = 0;

    // Show reconnecting state
    this.setState('reconnecting');
    this.emit('reconnecting', { attempt: 1, delay: 0 });

    try {
      await this.doConnect();
      this.emit('reconnected', undefined);
    } catch (error) {
      this.log('Reconnect now failed:', error);
      // Resume normal reconnection schedule
      this.scheduleReconnect();
    }
  }

  /**
   * Send a message to a service
   */
  send(to: string, body: string | object): void {
    if (!this.isConnected() || !this.ws) {
      throw new Error('Not connected');
    }

    const bodyStr = typeof body === 'object' ? JSON.stringify(body) : body;

    const packet: GLPacket = {
      from: this.myGlid,
      to,
      type: PacketTypes.PAYLOAD,
      body: bodyStr,
    };

    this.sendPacket(packet);
  }

  /**
   * Send a command to a service
   */
  sendCommand(to: string, command: string): void {
    this.send(to, command);
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private async doConnect(): Promise<void> {
    this.setState('connecting');

    return new Promise((resolve, reject) => {
      let hasRejected = false;
      const rejectOnce = (error: Error) => {
        if (!hasRejected) {
          hasRejected = true;
          reject(error);
        }
      };

      const timeout = setTimeout(async () => {
        const diagnostic = diagnoseTimeout();
        this.emit('connectionDiagnostic', diagnostic);
        rejectOnce(new Error(diagnostic.message));
        this.handleConnectionFailure();
      }, CONNECTION.connectTimeout);

      try {
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          clearTimeout(timeout);
          this.log('WebSocket connected, authenticating...');
          this.authenticate(resolve, reject);
        };

        this.ws.onclose = async (event) => {
          clearTimeout(timeout);
          // Reject if we haven't connected yet (connection failed before open)
          if (this.state === 'connecting') {
            // Run diagnostics to determine the cause
            const diagnostic = await diagnoseConnectionFailure(
              this.url,
              new Error(`WebSocket closed: ${event.code} ${event.reason}`)
            );
            this.emit('connectionDiagnostic', diagnostic);
            rejectOnce(new Error(diagnostic.message));
          }
          this.handleClose(event);
        };

        this.ws.onerror = async (_event) => {
          clearTimeout(timeout);
          this.log('WebSocket error');

          // Run diagnostics to determine the cause
          const diagnostic = await diagnoseConnectionFailure(this.url);
          this.emit('connectionDiagnostic', diagnostic);

          const error = new Error(diagnostic.message);
          this.emit('error', error);
          rejectOnce(error);
        };

        this.ws.onmessage = async (event) => {
          // Handle both text and binary (Blob) messages
          let data: string;
          if (event.data instanceof Blob) {
            data = await event.data.text();
          } else {
            data = event.data;
          }
          this.handleMessage(data);
        };
      } catch (error) {
        clearTimeout(timeout);
        const diagnostic = diagnoseUnknown(error instanceof Error ? error : undefined);
        this.emit('connectionDiagnostic', diagnostic);
        rejectOnce(new Error(diagnostic.message));
        this.handleConnectionFailure();
      }
    });
  }

  private authenticate(resolve: () => void, reject: (error: Error) => void): void {
    this.setState('authenticating');
    this.authResolver = resolve;
    this.authRejecter = reject;

    const authTimeout = setTimeout(() => {
      this.authRejecter?.(new Error('Authentication timeout'));
      this.authResolver = null;
      this.authRejecter = null;
      this.disconnect();
    }, CONNECTION.authTimeout);

    // Store timeout for cleanup
    (this as { authTimeout?: ReturnType<typeof setTimeout> }).authTimeout = authTimeout;

    // Send auth packet
    const authPacket: GLPacket = {
      from: '',
      to: ServiceGLIDs.SYSTEM,
      type: PacketTypes.AUTH,
      body: `${this.username}:${this.password}`,
    };

    this.sendPacket(authPacket);
  }

  private handleMessage(data: string): void {
    this.lastMessageTime = Date.now();
    this.missedHeartbeats = 0;

    try {
      const packet = JSON.parse(data) as GLPacket;

      if (DEBUG.logMessages) {
        this.logRecv(packet);
      }

      switch (packet.type) {
        case PacketTypes.AUTH_OK:
          this.handleAuthOk(packet);
          break;

        case PacketTypes.PAYLOAD:
          this.handlePayload(packet);
          break;

        case PacketTypes.SERVICE:
          this.handleServiceMessage(packet);
          break;

        case PacketTypes.ERROR:
          this.handleError(packet);
          break;

        default:
          this.log('Unknown packet type:', packet.type);
      }
    } catch (error) {
      this.log('Failed to parse message:', error);
    }
  }

  private handleAuthOk(packet: GLPacket): void {
    // Clear auth timeout
    const authTimeout = (this as { authTimeout?: ReturnType<typeof setTimeout> }).authTimeout;
    if (authTimeout) {
      clearTimeout(authTimeout);
    }

    this.myGlid = packet.from;
    this.setState('connected');

    this.authResolver?.();
    this.authResolver = null;
    this.authRejecter = null;

    this.reconnectAttempt = 0;
    this.startHeartbeat();
    this.emit('connected', undefined);

    this.log('Authenticated as:', this.myGlid);
  }

  private handlePayload(packet: GLPacket): void {
    this.emit('message', {
      from: packet.from,
      body: packet.body,
    });
  }

  private handleServiceMessage(packet: GLPacket): void {
    this.emit('service', packet.body);
  }

  private handleError(packet: GLPacket): void {
    this.log('Server error:', packet.body);

    if (packet.body === 'bad_auth' || packet.body === 'not_auth') {
      // Emit auth failure diagnostic
      const diagnostic: ConnectionDiagnostic = {
        type: 'AUTH_FAILED',
        message: 'Invalid username or password',
        // No suggestion - cause is obvious
      };
      this.emit('connectionDiagnostic', diagnostic);

      this.authRejecter?.(new Error(`Authentication failed: ${packet.body}`));
      this.authResolver = null;
      this.authRejecter = null;
      this.disconnect();
    }

    this.emit('error', new Error(`Server error: ${packet.body}`));
  }

  private handleClose(event: CloseEvent): void {
    this.log('WebSocket closed:', event.code, event.reason);

    this.stopHeartbeat();
    this.ws = null;

    // Build appropriate error message based on close code/reason
    const errorMessage = this.getCloseErrorMessage(event.code, event.reason);

    // Emit diagnostic for auth failures during authentication
    const lowerReason = event.reason.toLowerCase();
    if (
      this.state === 'authenticating' &&
      (lowerReason === 'bad_auth' || lowerReason === 'not_auth' || event.code === 4004)
    ) {
      const diagnostic: ConnectionDiagnostic = {
        type: 'AUTH_FAILED',
        message: 'Invalid username or password',
        // No suggestion - cause is obvious
      };
      this.emit('connectionDiagnostic', diagnostic);
    }

    // Clear any pending auth
    this.authRejecter?.(new Error(errorMessage));
    this.authResolver = null;
    this.authRejecter = null;

    const wasConnected = this.state === 'connected';

    this.emit('disconnected', { code: event.code, reason: event.reason });

    // Auto-reconnect if enabled and was connected
    if (CONNECTION.reconnect && wasConnected && event.code !== 1000) {
      this.scheduleReconnect();
    } else {
      this.setState('disconnected');
    }
  }

  private handleConnectionFailure(): void {
    if (CONNECTION.reconnect) {
      this.scheduleReconnect();
    } else {
      this.setState('disconnected');
    }
  }

  private scheduleReconnect(): void {
    if (
      CONNECTION.reconnectAttempts > 0 &&
      this.reconnectAttempt >= CONNECTION.reconnectAttempts
    ) {
      this.log('Max reconnect attempts reached');
      this.setState('disconnected');
      this.emit('error', new Error('Max reconnect attempts reached'));
      return;
    }

    this.setState('reconnecting');
    this.reconnectAttempt++;

    // Exponential backoff
    const delay = Math.min(
      CONNECTION.reconnectDelay * Math.pow(2, this.reconnectAttempt - 1),
      CONNECTION.reconnectDelayMax
    );

    this.emit('reconnecting', {
      attempt: this.reconnectAttempt,
      delay,
    });

    this.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})`);

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.doConnect();
        this.emit('reconnected', undefined);
      } catch (error) {
        this.log('Reconnect failed:', error);
        this.scheduleReconnect();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.missedHeartbeats = 0;
    this.lastMessageTime = Date.now();

    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected()) return;

      // Check if we received any message since last heartbeat
      const silentDuration = Date.now() - this.lastMessageTime;
      if (silentDuration > CONNECTION.heartbeatInterval) {
        this.missedHeartbeats++;
        this.log(`Heartbeat: no response (missed ${this.missedHeartbeats}/${CONNECTION.heartbeatMissedMax})`);
      }

      if (this.missedHeartbeats >= CONNECTION.heartbeatMissedMax) {
        this.log('Connection stale — forcing reconnect');
        this.forceReconnect();
        return;
      }

      try {
        this.sendCommand(GLIDS.ECHO, Commands.pong());
      } catch {
        this.log('Heartbeat send failed — forcing reconnect');
        this.forceReconnect();
      }
    }, CONNECTION.heartbeatInterval);

    // Account keepalive: refreshes ACC cache TTL and syncs balance/profile
    this.accountKeepAliveTimer = setInterval(() => {
      if (!this.isConnected()) return;

      try {
        this.sendCommand(GLIDS.ACCOUNT, Commands.account());
      } catch {
        // Non-critical — heartbeat handles reconnection
      }
    }, CONNECTION.accountKeepAliveInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.accountKeepAliveTimer) {
      clearInterval(this.accountKeepAliveTimer);
      this.accountKeepAliveTimer = null;
    }
  }

  private forceReconnect(): void {
    this.stopHeartbeat();
    this.missedHeartbeats = 0;

    // Close the stale socket without triggering normal disconnect flow
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close();
      this.ws = null;
    }

    this.emit('disconnected', { code: 4000, reason: 'heartbeat_timeout' });
    this.scheduleReconnect();
  }

  /**
   * Get user-friendly error message from WebSocket close code/reason
   */
  private getCloseErrorMessage(code: number, reason: string): string {
    // Check for authentication errors (server sends these as close reasons)
    const lowerReason = reason.toLowerCase();
    if (lowerReason === 'bad_auth' || lowerReason === 'not_auth') {
      return 'Invalid username or password';
    }

    // Check close codes
    switch (code) {
      case 1000:
        return 'Connection closed';
      case 1006:
        return 'Unable to connect to server';
      case 4004:
        // Custom auth error code from poker_platform
        return 'Invalid username or password';
      default:
        if (reason) {
          return `Connection closed: ${reason}`;
        }
        return 'Connection closed';
    }
  }

  private sendPacket(packet: GLPacket): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not open');
    }

    const data = JSON.stringify(packet);

    if (DEBUG.logMessages) {
      this.logSend(packet);
    }

    this.ws.send(data);
  }

  private setState(newState: ConnectionState): void {
    if (this.state !== newState) {
      this.state = newState;

      if (DEBUG.logStateChanges) {
        this.log('State changed:', newState);
      }

      this.emit('stateChange', newState);
    }
  }

  private log(...args: unknown[]): void {
    if (DEBUG.logMessages || DEBUG.logStateChanges) {
      log.network.debug`${args.join(' ')}`;
    }
  }

  /**
   * Log outgoing message with consistent format
   */
  private logSend(packet: GLPacket): void {
    const timestamp = new Date().toISOString();
    let parsedBody: unknown = packet.body;
    try {
      parsedBody = JSON.parse(packet.body);
    } catch {
      // body is not JSON, keep as string
    }

    // Skip keepalive messages to reduce console noise
    if (packet.body === '# pong' || packet.body === '# account') return;

    const logEntry = {
      direction: '>>> SEND',
      timestamp,
      from: packet.from || '(empty)',
      to: packet.to,
      type: packet.type,
      payload: parsedBody,
    };

    log.network.debug`>>> ${packet.to}: ${JSON.stringify(logEntry)}`;
  }

  /**
   * Log incoming message with consistent format
   */
  private logRecv(packet: GLPacket): void {
    const timestamp = new Date().toISOString();
    let parsedBody: unknown = packet.body;
    try {
      parsedBody = JSON.parse(packet.body);
    } catch {
      // body is not JSON, keep as string
    }

    const logEntry = {
      direction: '<<< RECV',
      timestamp,
      from: packet.from,
      to: this.myGlid || '(me)',
      type: packet.type,
      payload: parsedBody,
    };

    log.network.debug`<<< ${packet.from}: ${JSON.stringify(logEntry)}`;
  }
}

/** Singleton network service instance */
export const networkService = NetworkService.getInstance();
